/**
 * Finance reducer slice. Handles:
 *   invoice.FsmTransitioned          — invoice FSM walk
 *   lc.ComposerOpened                — composer state
 *   lc.FundingMixSelected            — fundingMix
 *   lc.Issued                        — lcIssuance
 *   settlement.EventReceived         — settlementLegs
 *   reconciliation.ThreeFieldMatched — final 3-field match
 *   reputation.Updated               — reputation map + reputationBefore
 *
 * Pure function: `(state, ev) => state`.
 */

import type { CdmEvent, EventLogState, FundingMix } from "@/lib/sse/useSSE";

export function financeReducer(state: EventLogState, ev: CdmEvent): EventLogState {
  switch (ev.eventType) {
    case "invoice.FsmTransitioned": {
      const id = ev.payload.invoiceId as string;
      const to = ev.payload.to as string;
      if (!id || !to) return state;
      return { ...state, invoiceState: { ...state.invoiceState, [id]: to } };
    }
    case "lc.ComposerOpened": {
      return {
        ...state,
        lcComposer: {
          open: true,
          tenorOptionsDays: (ev.payload.tenorOptionsDays as number[] | undefined) ?? [30, 60, 120],
          tenorIndependent: (ev.payload.tenorIndependent as boolean | undefined) ?? true,
        },
      };
    }
    case "lc.FundingMixSelected": {
      return {
        ...state,
        fundingMix: {
          buyerWorkingCapitalPct: (ev.payload.buyerWorkingCapitalPct as number | undefined) ?? 0,
          bankFinancingPct:       (ev.payload.bankFinancingPct       as number | undefined) ?? 0,
          bankPartnerOrbid:       (ev.payload.bankPartnerOrbid       as string | null | undefined) ?? null,
          bankBadge:              (ev.payload.bankBadge              as "FAB" | "Itau" | null | undefined) ?? null,
        },
      };
    }
    case "lc.Issued": {
      return {
        ...state,
        lcIssuance: {
          lcReference:        (ev.payload.lcReference        as string)                  ?? "",
          amountUsd:          (ev.payload.amountUsd          as number)                  ?? 0,
          expiry:             (ev.payload.expiry             as string)                  ?? "",
          buyerSideBankBadge: (ev.payload.buyerSideBankBadge as "FAB" | "Itau" | null | undefined) ?? null,
          fundingMix:         (ev.payload.fundingMix         as FundingMix | null | undefined) ?? state.fundingMix,
          evidenceAnchor:     (ev.payload.evidenceAnchor     as { hcsTopicId?: string } | undefined),
          issuedAt:           new Date(ev.occurredAt).getTime(),
        },
      };
    }
    case "settlement.EventReceived": {
      return {
        ...state,
        settlementLegs: [
          ...state.settlementLegs,
          {
            leg:        (ev.payload.leg  as string) ?? "",
            rail:       (ev.payload.rail as string) ?? "Orbitra Global rails",
            occurredAt: new Date(ev.occurredAt).getTime(),
          },
        ],
      };
    }
    case "reconciliation.ThreeFieldMatched": {
      return {
        ...state,
        threeFieldMatched: {
          var:           Boolean(ev.payload.var),
          e2eId:         Boolean(ev.payload.e2eId),
          invoiceNumber: Boolean(ev.payload.invoiceNumber),
        },
      };
    }
    case "reputation.Updated": {
      const orbid = ev.payload.subjectOrbid as string;
      if (!orbid) return state;
      const after = ev.payload.after as Record<string, unknown> | undefined;
      const before = ev.payload.before as Record<string, unknown> | undefined;
      const nextReputation = after ? { ...state.reputation, [orbid]: after } : state.reputation;
      const nextBefore = before && !state.reputationBefore[orbid]
        ? { ...state.reputationBefore, [orbid]: before }
        : state.reputationBefore;
      return { ...state, reputation: nextReputation, reputationBefore: nextBefore };
    }
    default:
      return state;
  }
}
